/**
 * Lazy Loading Module
 * 
 * Provides functionality for efficiently loading images and background images on demand.
 * Supports both standard <img> elements and background images on any element.
 * Uses IntersectionObserver for performance and includes fallbacks for older browsers.
 * 
 * Features:
 * - Lazy loads both <img> elements and background images
 * - Supports gradients overlaid on background images
 * - Handles fade transitions with customizable duration
 * - Separates loading state from visibility state for advanced use cases
 * - Supports immediate loading via 'preload' class
 * 
 * Exports:
 * @function initLazyLoading - Initialize lazy loading for a given page
 * @function lazyLoadBackground - Load a background image with optional gradient
 * @function lazyLoadImage - Low-level image loading with options
 * @function showBackground - Make a loaded background visible (called automatically unless
 * the lazyLoadBackground(elem, {'showImmediately' : false})
 * @function hideBackground - Hide a background (useful for transitions)
 * 
 * Usage:
 * For images: <img class="lazyload" data-src="image.jpg">
 * For backgrounds: <div data-bg-url="image.jpg" data-gradient="linear-gradient(...)">
 * 
 * @returns {Function} Cleanup function to disconnect observers
 */

let lazyObserver;

const lazyLoadImage = (img, options = {}) => {
    return new Promise((resolve, reject) => {
        if (!img) {
            reject(new Error('No image element provided'));
            return;
        }

        const sourceUrl = img instanceof HTMLImageElement 
            ? img.dataset.src 
            : img.dataset.bgUrl;

        if (!sourceUrl) {
            reject(new Error('No source URL found'));
            return;
        }

        // If it's already loaded, resolve immediately
        if (img instanceof HTMLImageElement && !img.dataset.src) {
            resolve();
            return;
        }

        if (!(img instanceof HTMLImageElement) && img.dataset.bgLoaded === 'true') {
            // Still apply the gradient in case the image was already loaded offscreen.
            if (options.gradient) {
                const imageUrl = `url(${sourceUrl})`;
                const backgroundImage = `${options.gradient}, ${imageUrl}`;
                img.style.setProperty('--bg-image', backgroundImage);
            }
            resolve();
            return;
        }

        const tempImg = new Image();
        
        tempImg.onload = () => {
            // Set natural dimensions if not explicitly specified
            if (!img.width && !img.height) {
                img.width = tempImg.naturalWidth;
                img.height = tempImg.naturalHeight;
            }

            if (img instanceof HTMLImageElement) {
                img.src = sourceUrl;
                img.removeAttribute('data-src');
                img.classList.add('loaded');
            } else {
                // Handle background image
                const { gradient = null } = options;
                
                const imageUrl = `url(${sourceUrl})`;
                const backgroundImage = gradient 
                    ? `${gradient}, ${imageUrl}` 
                    : imageUrl;
                
                // Get current style
                const currentStyle = img.getAttribute('style') || '';
                
                // Add our new style while preserving existing styles
                const newStyle = `${currentStyle}; --bg-image: ${backgroundImage};`;
                
                // Set the complete style string
                img.setAttribute('style', newStyle);
                img.dataset.bgLoaded = 'true';

                resolve();
            }

            // console.log('Lazy loaded: ', img);
            resolve();
        };

        tempImg.onerror = (error) => {
            if (img instanceof HTMLImageElement) {
                img.classList.add('load-error');
            }
            reject(error);
        };

        tempImg.src = sourceUrl;
    });
};

const handleIntersection = (entries, observer) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            const element = entry.target;
            
            if (element instanceof HTMLImageElement) {
                lazyLoadImage(element)
                    .catch(error => console.error('Error loading image:', error));
            } else if (element.dataset.bgUrl) {
                const gradient = element.dataset.gradient;
                const options = gradient ? { gradient } : {};
                lazyLoadBackground(element, options)
                    .catch(error => console.error('Error loading background:', error));
            }
            
            observer.unobserve(element);
        }
    });
};

// Background image loader (now using the shared helper)
const lazyLoadBackground = async (element, options = {}) => {
    // Always prefer data attribute if it exists
    const gradientFromData = element.dataset.gradient?.replace(/;$/, '');  // Remove any trailing semicolon
    const gradient = gradientFromData || options.gradient;

    const loadedElement = await lazyLoadImage(element, { ...options, gradient });

    return loadedElement;
};

/*
 * Preload-fade init.
 *
 * Preloaded images are rendered eagerly (real src in HTML) — they're not
 * watched by the IntersectionObserver, but they DO get a `.preload-fade`
 * class for visual consistency with lazy-loaded images. This function
 * toggles `.is-loaded` once each image is actually painted so the CSS
 * opacity transition fires.
 *
 * Two paths:
 *   - Image is already complete (fast network / cached): defer the class
 *     swap to the next animation frame so the transition has a starting
 *     opacity:0 frame. Without rAF the browser collapses 0→1 within a
 *     single paint and the fade visibly skips.
 *   - Image not yet complete (slow network): wait for the load event,
 *     then add the class — fade plays normally.
 */
const initPreloadFades = (container = document) => {
    container.querySelectorAll('img.preload-fade').forEach(img => {
        if (img.complete && img.naturalWidth > 0) {
            requestAnimationFrame(() => img.classList.add('is-loaded'));
            return;
        }
        img.addEventListener('load',  () => img.classList.add('is-loaded'),  { once: true });
        img.addEventListener('error', () => img.classList.add('load-error'), { once: true });
    });
};

/*
 * Background-video fade-in.
 *
 * Counterpart to initPreloadFades but for <video class="bg-fade"> elements.
 * Adds `.is-loaded` once the browser fires `canplay` (enough data buffered
 * to start playback) — CSS transitions opacity from 0 to its final value.
 *
 * Two paths mirror initPreloadFades:
 *   - Already-ready (cached / fast network): readyState >= HAVE_CURRENT_DATA
 *     means the browser has enough to paint. Defer the class swap to the
 *     next animation frame so the transition fires from a starting frame
 *     of opacity:0 (without rAF the browser collapses 0→final within a
 *     single paint and the fade visibly skips).
 *   - Not yet ready (slow network): wait for `canplay`, then swap.
 *
 * Until the class is added, the placeholder image painted by .bg-panel--image
 * (data-bg-url ::before overlay) keeps the container filled — no FOUC.
 */
const initBgVideoFades = (container = document) => {
    container.querySelectorAll('video.bg-fade').forEach(video => {
        // HAVE_CURRENT_DATA = 2; first-frame painted is enough to fade in.
        if (video.readyState >= 2) {
            requestAnimationFrame(() => video.classList.add('is-loaded'));
            return;
        }
        video.addEventListener('canplay', () => video.classList.add('is-loaded'), { once: true });
        video.addEventListener('error',   () => video.classList.add('load-error'), { once: true });
    });
};

// Function to initialize lazy loading for dynamically added content
const initializeDynamicContent = (container = document) => {
    // Create observer if it doesn't exist
    if (!lazyObserver && 'IntersectionObserver' in window) {
        lazyObserver = new IntersectionObserver(handleIntersection, {
            rootMargin: '50px 0px',
            threshold: 0.01
        });
    }
    
    if (!lazyObserver) {
        // Fallback for browsers without IntersectionObserver
        const lazyImages = container.querySelectorAll('img.lazyload');
        const lazyBackgrounds = container.querySelectorAll('[data-bg-url]');
        
        lazyImages.forEach(img => lazyLoadImage(img));
        lazyBackgrounds.forEach(element => lazyLoadBackground(element));
        return;
    }

    // Initialize new images
    const lazyImages = container.querySelectorAll('img.lazyload');
    lazyImages.forEach(img => {
        if (img.classList.contains('preload')) {
            lazyLoadImage(img)
                .catch(error => console.error('Error loading image:', error));
        } else {
            lazyObserver.observe(img);
        }
    });

    // Initialize new background images
    const lazyBackgrounds = container.querySelectorAll('[data-bg-url]');
    lazyBackgrounds.forEach(element => {
        const gradient = element.dataset.gradient;
        const options = gradient ? { gradient } : {};

        if (element.classList.contains('preload')) {
            lazyLoadBackground(element, options)
                .catch(error => console.error('Error loading background:', error));
        } else {
            lazyObserver.observe(element);
        }
    });

    // Wire fade-in for eagerly-rendered preloaded images. Separate path from
    // the observer above because these aren't lazy-watched.
    initPreloadFades(container);

    // Wire fade-in for background videos that should reveal themselves
    // once enough data is buffered to play. Same shape as the image
    // preload fade — different event (`canplay`) and readiness check
    // (`readyState`) since videos don't expose `complete`.
    initBgVideoFades(container);
};

const initLazyLoading = () => {
    const cleanup = () => {
        if (lazyObserver) {
            lazyObserver.disconnect();
            lazyObserver = null;
        }
    };

    document.addEventListener("DOMContentLoaded", () => {
        initializeDynamicContent();
    });

    return cleanup;
};

export { 
    initLazyLoading, 
    lazyLoadBackground, 
    lazyLoadImage, 
    initializeDynamicContent };